/**
 * The formatting commands, as pure text transforms.
 *
 * Every one takes the document and a selection and returns the smallest edit
 * that achieves the change — a range to replace, the replacement, and where the
 * caret should end up. Nothing here touches the DOM; `applyEdit` in
 * ./textarea-edit performs the result.
 *
 * Keeping the edits minimal is not tidiness. The browser's undo stack groups by
 * what actually changed, so replacing one line when one line changed gives
 * sensible undo granularity, while rewriting the whole document to add a `> `
 * would make a single ⌘Z discard the entire sermon.
 */

import { slideContainerExpression } from "@/lib/slides";

export interface TextSelection {
  start: number;
  end: number;
}

export interface Edit {
  /** Range in the current document to replace. */
  start: number;
  end: number;
  text: string;
  /** Where the selection should sit afterwards, in the resulting document. */
  caret: TextSelection;
}

const BULLET = /^(\s*)([-+*])([ \t]+)/;
const ORDERED = /^(\s*)(\d{1,9})([.)])([ \t]+)/;
const HEADING = /^(\s*)(#{1,6})([ \t]+)/;
const QUOTE = /^(\s*)((?:>[ \t]?)+)/;
const TASK = /^(\s*[-+*][ \t]+)(\[[ xX]\])([ \t]+)/;
const FENCE = /^\s*(`{3,}|~{3,})/;

/** Start of the line containing `offset`. */
function lineStart(text: string, offset: number): number {
  const index = text.lastIndexOf("\n", Math.max(0, offset - 1));
  return index === -1 ? 0 : index + 1;
}

/** End of the line containing `offset`, excluding its newline. */
function lineEnd(text: string, offset: number): number {
  const index = text.indexOf("\n", offset);
  return index === -1 ? text.length : index;
}

/**
 * The full lines a selection touches.
 *
 * A selection dragged down to the start of the next line visually stops at the
 * end of the previous one, so it must not pull that next line into a block
 * operation.
 */
export function selectedLines(text: string, selection: TextSelection): TextSelection {
  const tail =
    selection.end > selection.start && text[selection.end - 1] === "\n"
      ? selection.end - 1
      : selection.end;
  return { start: lineStart(text, selection.start), end: lineEnd(text, tail) };
}

/** Rewrite each selected line, keeping the selection over the same lines. */
function mapLines(
  text: string,
  selection: TextSelection,
  transform: (line: string, index: number) => string,
): Edit {
  const block = selectedLines(text, selection);
  const lines = text.slice(block.start, block.end).split("\n");
  const next = lines.map(transform).join("\n");
  return {
    start: block.start,
    end: block.end,
    text: next,
    caret: { start: block.start, end: block.start + next.length },
  };
}

/** Lines that carry content, i.e. the ones a toggle should agree about. */
function meaningful(lines: string[]): string[] {
  const filled = lines.filter((line) => line.trim().length > 0);
  return filled.length ? filled : lines;
}

function stripBlockMarkers(line: string): string {
  return line.replace(HEADING, "$1").replace(BULLET, "$1").replace(ORDERED, "$1");
}

export function setHeading(text: string, selection: TextSelection, level: number): Edit {
  return mapLines(text, selection, (line) => {
    const bare = stripBlockMarkers(line);
    if (level === 0) return bare;
    const indent = /^\s*/.exec(bare)?.[0] ?? "";
    return `${indent}${"#".repeat(level)} ${bare.slice(indent.length)}`;
  });
}

export function toggleBlockquote(text: string, selection: TextSelection): Edit {
  const block = selectedLines(text, selection);
  const lines = text.slice(block.start, block.end).split("\n");
  const quoted = meaningful(lines).every((line) => QUOTE.test(line));

  return mapLines(text, selection, (line) =>
    quoted
      ? line.replace(QUOTE, (_whole, indent: string, markers: string) =>
          // Drop one level of nesting rather than all of them.
          `${indent}${markers.replace(/>[ \t]?/, "")}`,
        )
      : `> ${line}`,
  );
}

export function toggleBulletList(text: string, selection: TextSelection): Edit {
  const block = selectedLines(text, selection);
  const lines = text.slice(block.start, block.end).split("\n");
  const bulleted = meaningful(lines).every((line) => BULLET.test(line));

  return mapLines(text, selection, (line) => {
    if (bulleted) return line.replace(BULLET, "$1");
    const bare = line.replace(ORDERED, "$1");
    const indent = /^\s*/.exec(bare)?.[0] ?? "";
    return `${indent}- ${bare.slice(indent.length)}`;
  });
}

export function toggleOrderedList(text: string, selection: TextSelection): Edit {
  const block = selectedLines(text, selection);
  const lines = text.slice(block.start, block.end).split("\n");
  const numbered = meaningful(lines).every((line) => ORDERED.test(line));
  let counter = 0;

  return mapLines(text, selection, (line) => {
    if (numbered) return line.replace(ORDERED, "$1");
    const bare = line.replace(BULLET, "$1");
    if (!bare.trim()) return bare;
    counter += 1;
    const indent = /^\s*/.exec(bare)?.[0] ?? "";
    return `${indent}${counter}. ${bare.slice(indent.length)}`;
  });
}

export function toggleCodeBlock(text: string, selection: TextSelection): Edit {
  const block = selectedLines(text, selection);
  const body = text.slice(block.start, block.end);
  const lines = body.split("\n");

  if (lines.length >= 2 && FENCE.test(lines[0]) && FENCE.test(lines[lines.length - 1])) {
    const inner = lines.slice(1, -1).join("\n");
    return {
      start: block.start,
      end: block.end,
      text: inner,
      caret: { start: block.start, end: block.start + inner.length },
    };
  }

  const fenced = `\`\`\`\n${body}\n\`\`\``;
  return {
    start: block.start,
    end: block.end,
    // Put the caret inside the fence, where the next keystroke belongs.
    text: fenced,
    caret: { start: block.start + 4, end: block.start + 4 + body.length },
  };
}

/** A heading line, with the `{slide}` marker split off if it carries one. */
const HEADING_SLIDE = /^( {0,3}#{1,6}[ \t]+)(.*?)([ \t]*\{[ \t]*slide[ \t]*\})?([ \t]*)$/i;

/** The `:::slide` container the given lines sit inside, if any. */
function containerAround(text: string, block: TextSelection) {
  const expression = slideContainerExpression();
  let match = expression.exec(text);
  while (match) {
    const start = match.index;
    const end = start + match[0].length;
    if (block.start >= start && block.end <= end) {
      return { start, end, body: (match[3] ?? "").replace(/\n$/, "") };
    }
    if (match[0].length === 0) expression.lastIndex += 1;
    match = expression.exec(text);
  }
  return null;
}

/**
 * Make the selection a slide.
 *
 * Four gestures over the three slide syntaxes, and which one you get is not a
 * setting — it follows from what you selected:
 *
 * - **Inside a `:::slide` container**, it unwraps. Safe, because the words
 *   never left the manuscript, so there is nothing to lose.
 * - **On a heading**, it toggles a trailing `{slide}` marker. A section heading
 *   is already short enough to *be* the slide, and moving it into a block would
 *   take it out of the manuscript: the heading would disappear from the page
 *   you read from and from the reading view's contents list. Annotating leaves
 *   it exactly where it is, still a heading everywhere it is rendered, and adds
 *   a title card to the deck.
 * - **On any other selected text**, it wraps the lines in `:::slide`. This is
 *   the scripture case, and it used to be the bug: putting a quotation on the
 *   screen meant replacing it with a block, which took it out of the reading
 *   view, so the only way to both project it and read from it was to type it
 *   twice. Wrapping annotates instead — the passage stays a blockquote in the
 *   manuscript and the deck gets the same words.
 * - **On nothing**, it opens a ```` ```slide ```` block with a placeholder to
 *   type over. That is the one kind that leaves the manuscript, and the one
 *   kind that is right to: text written straight into a block was never part
 *   of what gets read aloud.
 */
export function makeSlide(text: string, selection: TextSelection): Edit {
  const block = selectedLines(text, selection);
  const taken = text.slice(block.start, block.end);

  // Checked before the heading rule: a heading inside a container is already on
  // that container's slide, so the gesture there means "undo this", not "mark
  // this as well".
  const container = containerAround(text, block);
  if (container) {
    return {
      start: container.start,
      end: container.end,
      text: container.body,
      caret: { start: container.start, end: container.start + container.body.length },
    };
  }

  const heading = taken.includes("\n") ? null : HEADING_SLIDE.exec(taken);
  if (heading) {
    // Everything after the heading's text: the marker if present, then any
    // trailing spaces. Replacing that span is the whole edit either way.
    const tailStart = block.start + heading[1].length + heading[2].length;
    const marked = Boolean(heading[3]);
    const inserted = marked ? "" : " {slide}";
    return {
      start: tailStart,
      end: block.end,
      text: inserted,
      caret: {
        start: tailStart + inserted.length,
        end: tailStart + inserted.length,
      },
    };
  }

  // Sit the slide in its own paragraph. A marker line pressed up against the
  // prose above it still parses, but the manuscript would then keep whatever
  // run-together spacing the cut left behind.
  const before = block.start === 0 || /\n\n$/.test(text.slice(0, block.start)) ? "" : "\n\n";
  const after = block.end >= text.length || /^\n\n/.test(text.slice(block.end)) ? "" : "\n\n";

  if (taken.trim()) {
    const inserted = `${before}:::slide\n${taken}\n:::${after}`;
    // The selection stays over the same words, which is the point: they have
    // not moved, they have only been marked.
    const bodyStart = block.start + before.length + ":::slide\n".length;
    return {
      start: block.start,
      end: block.end,
      text: inserted,
      caret: { start: bodyStart, end: bodyStart + taken.length },
    };
  }

  const title = "Slide title";
  const inserted = `${before}\`\`\`slide\n# ${title}\n\`\`\`${after}`;

  // Caret over the heading, so typing replaces the placeholder.
  const titleStart = block.start + before.length + "```slide\n# ".length;
  return {
    start: block.start,
    end: block.end,
    text: inserted,
    caret: { start: titleStart, end: titleStart + title.length },
  };
}

/**
 * Wrap or unwrap the selection in an inline marker (`**`, `*`, `` ` ``).
 *
 * Checks outside the selection before inside it: double-clicking a bold word
 * selects the word, not its asterisks, and unbolding is what the author means.
 */
export function toggleInline(text: string, selection: TextSelection, marker: string): Edit {
  const { start, end } = selection;
  const width = marker.length;

  if (
    start >= width &&
    end + width <= text.length &&
    text.slice(start - width, start) === marker &&
    text.slice(end, end + width) === marker
  ) {
    const inner = text.slice(start, end);
    return {
      start: start - width,
      end: end + width,
      text: inner,
      caret: { start: start - width, end: start - width + inner.length },
    };
  }

  const selected = text.slice(start, end);
  if (selected.length >= width * 2 && selected.startsWith(marker) && selected.endsWith(marker)) {
    const inner = selected.slice(width, -width);
    return { start, end, text: inner, caret: { start, end: start + inner.length } };
  }

  return {
    start,
    end,
    text: `${marker}${selected}${marker}`,
    caret: { start: start + width, end: start + width + selected.length },
  };
}

export function insertLink(text: string, selection: TextSelection, url: string): Edit {
  const label = text.slice(selection.start, selection.end) || "link";
  const inserted = `[${label}](${url})`;
  return {
    start: selection.start,
    end: selection.end,
    text: inserted,
    // Select the URL — the label is usually right and the URL usually isn't.
    caret: {
      start: selection.start + label.length + 3,
      end: selection.start + label.length + 3 + url.length,
    },
  };
}

/**
 * Drop a footnote at the caret.
 *
 * The note is written inline as `^[…]` — the same form it is stored in — so
 * there is no second surface to edit it in and nothing to keep in sync. The
 * caret lands between the brackets, ready for the citation.
 */
export function insertFootnote(text: string, selection: TextSelection, content = ""): Edit {
  const inserted = `^[${content}]`;
  const inside = selection.start + 2;
  return {
    start: selection.start,
    end: selection.end,
    text: inserted,
    caret: { start: inside, end: inside + content.length },
  };
}

/**
 * Enter inside a list or blockquote.
 *
 * Returns null when the line carries no marker, leaving Enter to the browser —
 * which is the common case, and one keystroke of prose should never route
 * through JavaScript that could get it wrong.
 */
export function continueBlock(text: string, selection: TextSelection): Edit | null {
  if (selection.start !== selection.end) return null;

  const start = lineStart(text, selection.start);
  const line = text.slice(start, lineEnd(text, selection.start));
  const beforeCaret = text.slice(start, selection.start);

  const task = TASK.exec(line);
  const bullet = BULLET.exec(line);
  const ordered = ORDERED.exec(line);
  const quote = QUOTE.exec(line);
  if (!task && !bullet && !ordered && !quote) return null;

  const prefixLength = task
    ? task[0].length
    : bullet
      ? bullet[0].length
      : ordered
        ? ordered[0].length
        : quote![0].length;

  // Caret still inside the marker itself — treat Enter as an ordinary break.
  if (beforeCaret.length < prefixLength) return null;

  // An empty item ends the list rather than producing another empty one.
  if (line.slice(prefixLength).trim() === "") {
    return { start, end: selection.start, text: "", caret: { start, end: start } };
  }

  let marker: string;
  if (task) marker = `${task[1]}[ ]${task[3]}`;
  else if (bullet) marker = `${bullet[1]}${bullet[2]}${bullet[3]}`;
  else if (ordered)
    marker = `${ordered[1]}${Number(ordered[2]) + 1}${ordered[3]}${ordered[4]}`;
  else marker = `${quote![1]}${quote![2]}`;

  const inserted = `\n${marker}`;
  const caret = selection.start + inserted.length;
  return {
    start: selection.start,
    end: selection.start,
    text: inserted,
    caret: { start: caret, end: caret },
  };
}

/** Tab and Shift-Tab over the selected lines. */
export function indentLines(
  text: string,
  selection: TextSelection,
  direction: 1 | -1,
): Edit {
  return mapLines(text, selection, (line) =>
    direction === 1 ? `  ${line}` : line.replace(/^(\t| {1,2})/, ""),
  );
}

/**
 * Whether Tab should indent rather than move focus.
 *
 * Tab is how someone using only a keyboard leaves the editor, so it is only
 * taken when the intent is unmistakable: inside a list, or with more than one
 * line selected.
 */
export function tabShouldIndent(text: string, selection: TextSelection): boolean {
  if (selection.start !== selection.end) {
    const block = selectedLines(text, selection);
    if (text.slice(block.start, block.end).includes("\n")) return true;
  }
  const line = text.slice(lineStart(text, selection.start), lineEnd(text, selection.start));
  return BULLET.test(line) || ORDERED.test(line);
}
